/** The raw Stripe dates the Standard renewal date is derived from. */
export type SubscriptionRenewalDates = {
  startDate?: string | Date | null;
  trialEndDate?: string | Date | null;
};

export type SubscriptionBillingFields = SubscriptionRenewalDates & {
  nextBillingDate?: string | Date | null;
};

export type NextPaymentDateResolution = {
  rawValue: string;
  source: 'startDate' | 'trialEndDate' | 'nextBillingDate';
  date: Date;
  /** Long formatted label, or rawValue when the date is in the past. */
  displayLabel: string;
  isPast: boolean;
};

/** Fallback when legacy Standard agencies have no trial end on file. */
export const STANDARD_PLAN_RENEWAL_FALLBACK_DATE = new Date(2026, 8, 30);

const startOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Parse GraphQL date/time values using calendar date parts (UTC when timestamped). */
export const parseSubscriptionDate = (value: string | Date): Date | null => {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const dateParts = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (dateParts) {
    const year = Number(dateParts[1]);
    const month = Number(dateParts[2]);
    const day = Number(dateParts[3]);
    const local = new Date(year, month - 1, day);
    return isNaN(local.getTime()) ? null : local;
  }
  const parsed = new Date(trimmed);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const buildResolution = (
  rawValue: string,
  source: NextPaymentDateResolution['source'],
  date: Date
): NextPaymentDateResolution => {
  const isPast = startOfLocalDay(date) < startOfLocalDay(new Date());
  if (isPast) {
    // eslint-disable-next-line no-console
    console.warn('[renewal] next payment date is in the past', {
      source,
      rawValue,
      parsedIso: date.toISOString(),
      displayWeekday: date.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })
    });
    return {
      rawValue,
      source,
      date,
      displayLabel: rawValue,
      isPast: true
    };
  }
  return {
    rawValue,
    source,
    date,
    displayLabel: date.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }),
    isPast: false
  };
};

const tryCandidate = (
  raw: string | Date | null | undefined,
  source: NextPaymentDateResolution['source']
): NextPaymentDateResolution | null => {
  if (raw == null || raw === '') {
    return null;
  }
  const rawValue = String(raw);
  const date = parseSubscriptionDate(rawValue);
  if (!date) {
    return null;
  }
  if (startOfLocalDay(date) >= startOfLocalDay(new Date())) {
    return buildResolution(rawValue, source, date);
  }
  return null;
};

const parseField = (value?: string | Date | null): Date | null =>
  value == null || value === '' ? null : parseSubscriptionDate(String(value));

const getRenewalAnchorDate = (subscription?: SubscriptionRenewalDates | null): Date | null => {
  const startDate = parseField(subscription?.startDate);
  const trialEndDate = parseField(subscription?.trialEndDate);
  if (trialEndDate != null && (startDate == null || trialEndDate > startDate)) {
    return trialEndDate;
  }
  return startDate;
};

/**
 * End of the Standard term one calendar year on from the anchor, floored at
 * the start date so a stale trial end can never place it before the
 * subscription itself began. Does not roll forward across multiple years:
 * the migration window derived from this has no upper bound, so a term end
 * from any point in the past still opens it.
 */
const getStandardPlanTermEndDate = (subscription?: SubscriptionRenewalDates | null): Date => {
  const anchor = getRenewalAnchorDate(subscription);
  if (!anchor) {
    return STANDARD_PLAN_RENEWAL_FALLBACK_DATE;
  }

  const startDate = parseField(subscription?.startDate);
  const termEnd = new Date(anchor);
  termEnd.setFullYear(termEnd.getFullYear() + 1);
  return startDate != null && startDate > termEnd ? startDate : termEnd;
};

/**
 * End of the agency's current Standard term, which is when a sunset renewal
 * takes effect and is first charged: one calendar year on from the trial end,
 * or from the subscription start for the agencies that never had a trial.
 * Rolls forward to the next future anniversary so the displayed date is
 * always upcoming, never one that has already passed.
 *
 * Floored at the start date, so a stale trial end can never place the renewal
 * before the subscription itself began.
 */
export const getStandardPlanRenewalDate = (
  subscription?: SubscriptionRenewalDates | null
): Date => {
  const startDate = parseField(subscription?.startDate);
  const anchor = getRenewalAnchorDate(subscription);
  if (!anchor) {
    return STANDARD_PLAN_RENEWAL_FALLBACK_DATE;
  }

  const now = new Date();
  const renewal = new Date(anchor);
  while (renewal <= now) {
    renewal.setFullYear(renewal.getFullYear() + 1);
  }
  return startDate != null && startDate > renewal ? startDate : renewal;
};

/**
 * First day the migration banner may appear: `days` before the term end, but
 * never before the subscription started.
 */
export const getRenewalPromptStartDate = (
  subscription: SubscriptionRenewalDates | null | undefined,
  days: number
): Date => {
  const windowStart = new Date(getStandardPlanTermEndDate(subscription));
  windowStart.setDate(windowStart.getDate() - days);

  const startDate = parseField(subscription?.startDate);
  return startDate != null && startDate > windowStart ? startDate : windowStart;
};

/**
 * Resolve the next payment date from subscription GraphQL fields only.
 * Prefers nextBillingDate for post-trial subscriptions; uses trialEndDate
 * while the trial is still active.
 */
export const resolveNextPaymentDate = (
  subscription?: SubscriptionBillingFields | null
): NextPaymentDateResolution | null => {
  if (!subscription) {
    // eslint-disable-next-line no-console
    console.warn('[renewal] no subscription on agency user');
    return null;
  }

  const trialEndRaw = subscription.trialEndDate;
  const nextBillingRaw = subscription.nextBillingDate;
  const trialEnd = trialEndRaw ? parseSubscriptionDate(String(trialEndRaw)) : null;
  const trialStillActive =
    trialEnd != null && startOfLocalDay(trialEnd) >= startOfLocalDay(new Date());

  if (trialStillActive && trialEndRaw) {
    return buildResolution(String(trialEndRaw), 'trialEndDate', trialEnd);
  }

  const fromNextBilling = tryCandidate(nextBillingRaw, 'nextBillingDate');
  if (fromNextBilling) {
    return fromNextBilling;
  }

  const fromTrialEnd = tryCandidate(trialEndRaw, 'trialEndDate');
  if (fromTrialEnd) {
    return fromTrialEnd;
  }

  const fallbackRaw =
    nextBillingRaw != null && nextBillingRaw !== ''
      ? String(nextBillingRaw)
      : trialEndRaw != null && trialEndRaw !== ''
        ? String(trialEndRaw)
        : null;
  if (!fallbackRaw) {
    // eslint-disable-next-line no-console
    console.warn('[renewal] no trialEndDate or nextBillingDate on subscription');
    return null;
  }

  const fallbackSource =
    nextBillingRaw != null && nextBillingRaw !== '' ? 'nextBillingDate' : 'trialEndDate';
  const fallbackDate = parseSubscriptionDate(fallbackRaw);
  if (!fallbackDate) {
    // eslint-disable-next-line no-console
    console.warn('[renewal] could not parse subscription billing date', {
      source: fallbackSource,
      rawValue: fallbackRaw
    });
    return {
      rawValue: fallbackRaw,
      source: fallbackSource,
      date: new Date(NaN),
      displayLabel: fallbackRaw,
      isPast: true
    };
  }
  return buildResolution(fallbackRaw, fallbackSource, fallbackDate);
};

/**
 * Next charge date for legacy Standard → Basic/Pro sunset renewals.
 * Always matches the migration banner renewal date (not Stripe nextBillingDate).
 */
export const resolveSunsetRenewalPaymentDate = (
  subscription?: SubscriptionBillingFields | null
): NextPaymentDateResolution | null => {
  if (!subscription) {
    // eslint-disable-next-line no-console
    console.warn('[renewal] no subscription on agency user');
    return null;
  }

  const renewalDate = getStandardPlanRenewalDate(subscription);
  const usesTrialEnd = subscription.trialEndDate != null && subscription.trialEndDate !== '';
  const anchor = usesTrialEnd ? subscription.trialEndDate : subscription.startDate;
  const rawValue = anchor != null && anchor !== '' ? String(anchor) : renewalDate.toISOString();
  return buildResolution(rawValue, usesTrialEnd ? 'trialEndDate' : 'startDate', renewalDate);
};
